import classNames from 'classnames'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Body2, Heading4 } from '@ui/Typography'
import { Checkbox } from '@ui/components/Checkbox'

import {
  isPlatformExternalApplicationACUITY,
  isPlatformExternalApplicationFXBLUE,
} from '@model/PlatformInfoDto'
import { OperatingSystemEnum } from '@model/PlatformTypeEnum'

import {
  MTDownloadIcon,
  ManualIconFactory,
  PlatformDownloadButton,
  PlatformIconFactory,
} from './PlatformIcon'
import { usePlatformInfo } from './Platforms'

import styles from './Platforms.module.scss'

enum ToolkitType {
  ACUITY = 'acuity',
  MT = 'mt',
}

export const PlatformsToolKitsSection: React.FC<ReturnType<typeof usePlatformInfo>> = (props) => {
  const { selectedPlatform } = props

  const { t } = useTranslation()

  const [isAcuitySelected, setIsAcuitySelected] = useState(false)
  const [isMtSelected, setIsMtSelected] = useState(false)

  const handleSetToolkitType = (checked: boolean, toolkitType: ToolkitType) => {
    if (toolkitType === ToolkitType.MT) {
      setIsMtSelected(checked)
    } else {
      setIsAcuitySelected(checked)
    }
  }

  return (
    <div className={styles.toolKits}>
      {selectedPlatform?.externalPlatformApplications.map((application, applicationIndex) => {
        const isSelected = isPlatformExternalApplicationFXBLUE(application)
          ? isMtSelected
          : isAcuitySelected

        return (
          <div className={classNames(styles.toolkit)} key={applicationIndex}>
            <Heading4 className={styles.title}>
              {isPlatformExternalApplicationFXBLUE(application) && (
                <span>{t('MetaTrader.FxBlue')}</span>
              )}
              {isPlatformExternalApplicationACUITY(application) && (
                <span>{t('MetaTrader.Acuity')}</span>
              )}
            </Heading4>
            <Body1
              isParagraph
              className={classNames(styles.text, styles.subtitle, styles.textGray)}
            >
              {isPlatformExternalApplicationFXBLUE(application) && (
                <span>{t('MetaTrader.FXBlueDesc')}</span>
              )}
              {isPlatformExternalApplicationACUITY(application) && (
                <span>{t('MetaTrader.AcuityDesc')}</span>
              )}
            </Body1>
            <div className={classNames(styles.icons)}>
              {isSelected && (
                <div className={styles.icon}>
                  <PlatformDownloadButton to={application.productLink}>
                    <PlatformIconFactory os={OperatingSystemEnum.WINDOWS} />
                  </PlatformDownloadButton>
                </div>
              )}
              {!isSelected && (
                <div className={styles.icon}>
                  <PlatformDownloadButton disabled>
                    <PlatformIconFactory os={OperatingSystemEnum.WINDOWS} />
                  </PlatformDownloadButton>
                </div>
              )}

              {application.guides.map((guid, guideIndex) =>
                isSelected ? (
                  <div className={styles.icon} key={guideIndex}>
                    <PlatformDownloadButton to={guid.guideLink} disabled={!isSelected}>
                      <MTDownloadIcon
                        title={guid.name}
                        icon={<ManualIconFactory icon={guid.icon} />}
                      />
                    </PlatformDownloadButton>
                  </div>
                ) : (
                  <div className={styles.icon} key={guideIndex}>
                    <PlatformDownloadButton key={guideIndex} disabled>
                      <MTDownloadIcon
                        title={guid.name}
                        icon={<ManualIconFactory icon={guid.icon} />}
                      />
                    </PlatformDownloadButton>
                  </div>
                )
              )}
            </div>
            <Body2 isParagraph className={classNames(styles.textGray)}>
              {t('MetaTrader.More')}{' '}
              <a className='is-link' href={application.infoLink} target='_blank' rel='noreferrer'>
                {t('here')}
              </a>
            </Body2>
            <div className={styles.consent}>
              <Checkbox
                checked={
                  isPlatformExternalApplicationFXBLUE(application) ? isMtSelected : isAcuitySelected
                }
                onChange={(event) => {
                  handleSetToolkitType(
                    event,
                    isPlatformExternalApplicationFXBLUE(application)
                      ? ToolkitType.MT
                      : ToolkitType.ACUITY
                  )
                }}
              >
                {isPlatformExternalApplicationFXBLUE(application) && (
                  <span>{t('MetaTrader.FXBlueDownload')}</span>
                )}
                {isPlatformExternalApplicationACUITY(application) && (
                  <span>{t('MetaTrader.AcuityDownload')}</span>
                )}
              </Checkbox>
            </div>
          </div>
        )
      })}
    </div>
  )
}
